/**
 * NotificationContext - Zentrale Verwaltung für Toast-Benachrichtigungen
 *
 * Stellt addToast, dismissToast und notify bereit.
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

// Context erstellen
const NotificationContext = createContext(null);

// Toast ID Counter
let toastIdCounter = 0;

/**
 * NotificationProvider - Wrapper für die App
 *
 * Verwendung:
 * <NotificationProvider>
 *   <App />
 * </NotificationProvider>
 */
export function NotificationProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  // Toast hinzufügen
  const addToast = useCallback((toast) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Toast Debug] addToast called with:', toast);
    }
    const id = ++toastIdCounter;
    const newToast = {
      id,
      icon: toast.icon || null,
      message: toast.message || '',
      duration: toast.duration || 2000,
      priority: toast.priority || 'normal',
      style: toast.style || 'default',
      timestamp: Date.now()
    };

    if (process.env.NODE_ENV === 'development') {
      console.log('[Toast Debug] Created newToast:', newToast);
    }

    setToasts(prev => {
      // Maximal 5 Toasts, älteste entfernen wenn nötig
      const updated = [...prev, newToast];
      if (process.env.NODE_ENV === 'development') {
        console.log('[Toast Debug] Updated toasts array length:', updated.length);
      }
      if (updated.length > 5) {
        return updated.slice(-5);
      }
      return updated;
    });

    return id;
  }, []);

  // Toast entfernen
  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // Alle Toasts entfernen
  const clearAll = useCallback(() => {
    setToasts([]);
  }, []);

  // Einfache Text-Benachrichtigung
  const notify = useCallback((message, options = {}) => {
    return addToast({
      message,
      icon: options.icon || null,
      duration: options.duration || 2000,
      priority: options.priority || 'normal',
      style: options.style || 'default'
    });
  }, [addToast]);

  // Memoized context value
  const value = useMemo(() => ({
    toasts,
    addToast,
    dismissToast,
    clearAll,
    notify
  }), [toasts, addToast, dismissToast, clearAll, notify]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

/**
 * useNotifications - Hook für Zugriff auf Notification-Funktionen
 *
 * Verwendung:
 * const { notify, toasts } = useNotifications();
 */
export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    // Fallback wenn Provider fehlt (für Standalone-Verwendung)
    console.warn('useNotifications: NotificationProvider not found, using no-op fallback');
    return {
      toasts: [],
      addToast: () => 0,
      dismissToast: () => {},
      clearAll: () => {},
      notify: () => 0
    };
  }
  return context;
}

export default NotificationContext;
